<template>
  <div class="wave-canvas-container">
    <div class="canvas-wrap">
      <canvas ref="canvas"></canvas>
    </div>
  </div>
</template>

<script>
export default {
  name: 'WaveCanvas',
  props: {
    // 是否启用声波显示
    enabled: {
      type: Boolean,
      default: true
    },
    // 透明度
    opacity: {
      type: Number,
      default: 0.6
    }
  },
  data() {
    return {
      isRunning: false,
      audioCtx: null,
      analyser: null,
      micStream: null,
      dataBuf: null,
      rafId: null,
      
      // 图形参数 - 与原wave保持一致
      barCount: 64, // 恢复原始柱子数量
      barGap: 2,
      brickH: 6, // 恢复原始砖块高度
      brickGap: 3, // 恢复原始砖块间隙
      
      // 信号平滑
      smoothing: 0.8,
      rmsSmooth: 0.92,
      rmsPrev: 0,
      
      // dB 参数（按wave的增益+13，阈值-33调整）
      minDb: -60,
      gainDb: 13, // 增益调整为+13
      thresholdDb: -33, // 阈值调整为-33
      
      // 颜色配置 - 使用原wave的精确颜色
      colors: [
        '#5aa7ff',   // lv1 - 原始颜色
        '#35c9ff',   // lv2  
        '#31d2a3',   // lv3
        '#ffd166',   // lv4
        '#ff6b6b'    // lv5
      ],
      colorOff: '#1b2747' // 关闭态颜色 - 原始颜色
    };
  },
  computed: {
    dpr() {
      return Math.min(window.devicePixelRatio || 1, 2);
    }
  },
  mounted() {
    this.resizeCanvas();
    window.addEventListener('resize', this.resizeCanvas, { passive: true });
    
    // 自动启动
    if (this.enabled) {
      this.initAudio();
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.resizeCanvas);
    this.stop();
  },
  watch: {
    enabled(newVal) {
      if (newVal) {
        this.initAudio();
      } else {
        this.stop();
      }
    }
  },
  methods: {
    async initAudio() {
      if (this.isRunning || !this.enabled) return;
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: true, 
          video: false 
        });
        
        this.micStream = stream;
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        
        analyser.fftSize = 1024; // 减小FFT大小，提高性能
        analyser.smoothingTimeConstant = 0.7;
        source.connect(analyser);
        
        this.audioCtx = ctx;
        this.analyser = analyser;
        this.dataBuf = new Float32Array(analyser.fftSize);
        this.isRunning = true;
        
        this.loop();
      } catch (e) {
        console.warn('WaveCanvas: 无法获取麦克风权限', e);
        // 静默失败，不影响主要功能
      }
    },
    
    stop() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      
      if (this.audioCtx) {
        try {
          this.audioCtx.close();
        } catch (e) {
          // 忽略关闭音频上下文时的错误
          console.warn('Error closing audio context:', e);
        }
        this.audioCtx = null;
      }
      
      if (this.micStream) {
        this.micStream.getTracks().forEach(t => t.stop());
        this.micStream = null;
      }
      
      this.analyser = null;
      this.isRunning = false;
      this.rmsPrev = 0;
      
      // 清空画布
      const cvs = this.$refs.canvas;
      if (cvs) {
        const ctx = cvs.getContext('2d');
        ctx.clearRect(0, 0, cvs.width, cvs.height);
      }
    },
    
    loop() {
      if (!this.isRunning || !this.analyser) return;
      this.rafId = requestAnimationFrame(this.loop);
      
      // 计算RMS
      this.analyser.getFloatTimeDomainData(this.dataBuf);
      const rms = this.computeRMS(this.dataBuf);
      const smoothRMS = this.rmsPrev * this.rmsSmooth + rms * (1 - this.rmsSmooth);
      this.rmsPrev = smoothRMS;
      
      // 计算dBFS
      let db = (smoothRMS > 0) ? 20 * Math.log10(smoothRMS) : -Infinity;
      db += this.gainDb;
      if (!isFinite(db)) db = this.minDb;
      
      // 绘制
      this.drawFrame(db);
    },
    
    computeRMS(a) {
      let s = 0;
      for (let i = 0; i < a.length; i++) {
        const v = a[i];
        s += v * v;
      }
      return Math.sqrt(s / a.length);
    },
    
    dbToUnit(db, minDb, maxDb) {
      const t = (db - minDb) / (maxDb - minDb);
      return Math.max(0, Math.min(1, t));
    },
    
    drawFrame(dbVal = null) {
      const cvs = this.$refs.canvas;
      if (!cvs) return;
      
      const ctx = cvs.getContext('2d');
      const w = cvs.clientWidth;
      const h = cvs.clientHeight;
      
      ctx.clearRect(0, 0, w, h);
      
      // 基线与尺寸
      const baseY = h - 5; // 离底部5px
      const n = this.barCount;
      const gap = this.barGap;
      const usableW = w - (n + 1) * gap;
      const barW = Math.max(1, usableW / n);
      
      // 砖块行数
      const brickH = this.brickH;
      const brickGap = this.brickGap;
      const maxBricks = Math.max(10, Math.floor((baseY - 14) / (brickH + brickGap)));
      const maxShowBricks = Math.min(50, maxBricks); // 恢复原始最大50块
      
      // ---- 阈值线（水平线）----
      const thrDb = this.thresholdDb; // 使用调整后的阈值-33dB
      const thrUnit = this.dbToUnit(thrDb, this.minDb, 0); // 0~1
      const thrY = baseY - (maxShowBricks * (brickH + brickGap)) * thrUnit; // 映射到高度
      ctx.save();
      ctx.strokeStyle = '#86a0ff'; // 原wave的阈值线颜色
      ctx.setLineDash([6, 6]); // 原wave的虚线样式
      ctx.lineWidth = 1.2; // 原wave的线宽
      ctx.beginPath();
      ctx.moveTo(0, Math.round(thrY) + 0.5);
      ctx.lineTo(w, Math.round(thrY) + 0.5);
      ctx.stroke();
      ctx.restore();
      
      // 当前dB对应亮块数
      let db = (dbVal == null) ? this.minDb : dbVal;
      const armed = db >= this.thresholdDb;
      
      let lit = 0;
      if (armed) {
        const t = (db - this.thresholdDb) / (0 - this.thresholdDb);
        const u = Math.max(0, Math.min(1, t));
        lit = Math.round(u * maxShowBricks);
      }
      
      // 绘制柱子
      const now = performance.now() / 1000;
      for (let i = 0; i < n; i++) {
        const x = gap + i * (barW + gap);
        const jitter = armed ? Math.max(-1, Math.min(1, Math.sin(i * 1.33 + now * 2.1) * 0.8)) : 0;
        const litI = Math.max(0, Math.min(maxShowBricks, lit + Math.round(jitter)));
        
        // 自底向上堆砖
        for (let b = 0; b < maxShowBricks; b++) {
          const y = baseY - (b + 1) * brickH - b * brickGap;
          const isOn = b < litI;
          
          let fill = this.colorOff;
          if (isOn) {
            const seg = Math.min(4, Math.floor(b / 10)); // 恢复原始每10块一个颜色段
            fill = this.colors[seg];
          }
          
          ctx.fillStyle = fill;
          this.roundRect(ctx, x, y, barW, brickH, Math.min(3, barW / 2)); // 使用原始圆角参数
          ctx.fill();
        }
      }
    },
    
    roundRect(ctx, x, y, w, h, r) {
      const rr = Math.min(r, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + rr, y);
      ctx.arcTo(x + w, y, x + w, y + h, rr);
      ctx.arcTo(x + w, y + h, x, y + h, rr);
      ctx.arcTo(x, y + h, x, y, rr);
      ctx.arcTo(x, y, x + w, y, rr);
      ctx.closePath();
    },
    
    resizeCanvas() {
      const cvs = this.$refs.canvas;
      if (!cvs) return;
      
      const container = cvs.parentElement;
      if (!container) return;
      
      const r = container.getBoundingClientRect();
      const w = Math.floor(r.width);
      const h = Math.floor(r.height);
      const s = this.dpr;
      
      cvs.width = Math.floor(w * s);
      cvs.height = Math.floor(h * s);
      cvs.style.width = `${w}px`;
      cvs.style.height = `${h}px`;
      
      const ctx = cvs.getContext('2d');
      ctx.setTransform(s, 0, 0, s, 0, 0);
      
      this.drawFrame();
    }
  }
};
</script>

<style scoped>
.wave-canvas-container {
  position: relative;
  width: 100%;
  height: 100%;
  /* 使用原wave的背景样式 */
  background: radial-gradient(ellipse at 50% -30%, rgba(106,169,255,.25), transparent 60%),
              linear-gradient(180deg, rgba(77,212,168,.08), transparent 40%), #0a0f1f;
  border: 1px solid #1e2a4a;
  border-radius: 14px;
  overflow: hidden;
}

.wave-canvas-container::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, rgba(255,255,255,0.03) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px);
  background-size: 24px 24px, 24px 24px;
  pointer-events: none;
}

canvas {
  display: block;
  width: 100%;
  height: 100%;
  margin-top: -110px;
}
</style>